import { createContext, useContext, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { errorCode } from './api/client'
import { useMe } from './api/queries'
import type { Policy, User } from './api/types'
import { Loading } from './components/ui'

/**
 * Текущий пользователь и действующие правила.
 *
 * Правила приходят вместе с профилем из /auth/me: они нужны формам для проверок
 * (горизонт, рабочие часы, максимальная длительность), и тянуть их отдельным запросом
 * с каждого экрана незачем.
 *
 * Права — две независимые оси, и смешивать их в одном поле нельзя:
 *   глобальная роль          — user.is_admin, user.can_recur;
 *   отношение к брони        — booking.my_role (организатор, участник, посторонний).
 */

interface SessionValue {
  user: User
  policy: Policy
}

const SessionContext = createContext<SessionValue | null>(null)

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession вызван вне защищённой части приложения')
  return value
}

/** Может ли человек бронировать: заблокированный смотрит, но не создаёт. */
export function useCanBook(): boolean {
  const { user } = useSession()
  return user.status === 'active' || user.status === 'pending'
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { data, isLoading, error } = useMe()

  if (isLoading) return <Loading text="Проверяем сессию…" />

  if (error) {
    const code = errorCode(error)
    if (code === 'unauthorized' || code === 'forbidden') {
      return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
    }
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <p className="text-base font-medium">Сервер не отвечает</p>
        <p className="mt-2 text-sm text-muted">
          Не удалось получить данные вашей учётной записи. Обновите страницу; если не помогает,
          сообщите администратору.
        </p>
      </div>
    )
  }

  if (!data) return <Loading />

  // Уволенный войти не должен вовсе — если это как-то случилось, сессия недействительна.
  if (data.user.status === 'deactivated') return <Navigate to="/blocked" replace />

  // Первый вход: без имени и фамилии человека не найти в списке участников.
  //
  // Условие целиком на сервере (needs_profile), и переписывать его здесь нельзя.
  // Именно так и разошлось: сервер требует имя И фамилию, а тут стояло «нет имени» —
  // человек, заполнивший одно имя, «Знакомство» проскакивал и оставался без фамилии,
  // то есть ненаходимым в поиске участников, которым его и зовут на встречи.
  if (data.needs_profile && location.pathname !== '/welcome') {
    return <Navigate to="/welcome" replace />
  }

  // Обратное правило обязательно, и вот почему. «Знакомство» показывается в AuthShell —
  // без меню и без единой ссылки наружу. Если человек попадёт туда с уже заполненным
  // профилем (вернулся кнопкой «назад», открыл сохранённый адрес, промахнулась гонка
  // обновления кеша), он увидит форму, которая ничего не изменит, и уйти ему некуда.
  if (!data.needs_profile && location.pathname === '/welcome') {
    return <Navigate to="/" replace />
  }

  return (
    <SessionContext.Provider value={{ user: data.user, policy: data.policy }}>
      {children}
    </SessionContext.Provider>
  )
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user } = useSession()
  if (!user.is_admin) {
    return (
      <div className="card px-6 py-12 text-center">
        <p className="text-base font-medium">Раздел доступен администраторам</p>
        <p className="mt-2 text-sm text-muted">
          Права выдаёт действующий администратор в разделе «Сотрудники».
        </p>
      </div>
    )
  }
  return <>{children}</>
}
